import { createReducer, on } from '@ngrx/store';
import * as PolicyActions from './policy.actions';

export interface PolicyState {
    data: any;
    loading: boolean;
    termsLoading: boolean;
    dcPolicyLoading: boolean;
    dcBillingLoading: boolean;
    commLoading: boolean;
    dmsLoading: boolean;
    claimsLoading: boolean;
    payPlanLoading: boolean;
    error: string | null;
    policyNumber: string | null;
    policyTerm?: string | null;
    terms: string[];
    termData: { term: string; startDate: string; endDate: string }[];
    effectiveDate: string | null;
}

export const initialState: PolicyState = {
    data: null,
    loading: false,
    termsLoading: false,
    dcPolicyLoading: false,
    dcBillingLoading: false,
    commLoading: false,
    dmsLoading: false,
    claimsLoading: false,
    payPlanLoading: false,
    error: null,
    policyNumber: null,
    policyTerm: null,
    terms: [],
    termData: [],
    effectiveDate: null,
};

export const policyReducer = createReducer(
    initialState,
    on(PolicyActions.loadPolicyDetails, (state, { policyNumber, policyTerm }) => ({
        ...state,
        loading: true,
        error: null,
        policyNumber,
        policyTerm: policyTerm ?? null,
    })),
    on(PolicyActions.storePolicyTerms, (state, { terms, termData }) => ({
        ...state,
        termsLoading: false,
        terms,
        termData,
    })),

    on(PolicyActions.loadPolicyDetailsFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),
    on(PolicyActions.updateNotes, (state, { policyNumber, notes }) => ({
        ...state,
        data: {
            ...state.data,
            [policyNumber]: {
                ...state.data?.[policyNumber],
                policySnapshot: {
                    ...state.data?.[policyNumber]?.policySnapshot,
                    notes,
                },
            },
        },
    })),
    on(PolicyActions.loadPolicyTermDetail, (state, { policyTerm }) => ({
        ...state,
        loading: true,
        error: null,
        policyTerm: policyTerm,
    })),
    on(PolicyActions.loadPolicyDetailsSuccess, (state, { data }) => {
        const policyNumber = data ? Object.keys(data)[0] : state.policyNumber;
        const searchTerms = Array.isArray(data) ? data : null; // searchPolicies returns array

        return {
            ...state,
            data: searchTerms
                ? {
                      ...state.data,
                      [policyNumber!]: {
                          ...state.data?.[policyNumber!],
                          policySnapshot: {
                              ...state.data?.[policyNumber!]?.policySnapshot,
                              term: searchTerms,
                          },
                      },
                  }
                : data,
            loading: false,
            policyNumber: policyNumber,
        };
    }),

    on(PolicyActions.loadPolicyTermDetailFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false,
    })),
    on(PolicyActions.resetPolicyState, () => ({ ...initialState })),
    // DCPolicy & DCBilling
    on(PolicyActions.loadPolicyData, (state, { policyNumber, policyTerm, effectiveDate }) => ({
        ...state,
        termsLoading: !effectiveDate,
        dcPolicyLoading: !!effectiveDate,
        dcBillingLoading: !!effectiveDate,
        commLoading: !!effectiveDate,
        dmsLoading: !!effectiveDate,
        claimsLoading: !!effectiveDate,
        error: null,
        policyNumber,
        policyTerm: policyTerm ?? state.policyTerm,
        effectiveDate: effectiveDate && effectiveDate !== 'unknown' ? effectiveDate : state.effectiveDate,
        data: !effectiveDate ? null : state.data,
        // Clear stale terms when starting a fresh policy load (no effectiveDate means
        // it's a new policy search, not a term switch). This prevents the terms dropdown
        // from briefly showing the previous policy's terms before the new ones arrive.
        terms: !effectiveDate ? [] : state.terms,
        termData: !effectiveDate ? [] : state.termData,
    })),
    on(PolicyActions.loadDCPolicySuccess, (state, { policyNumber, policySnapshot }) => {
        const newState = {
            ...state,
            dcPolicyLoading: false,
            data: {
                ...state.data,
                [policyNumber]: {
                    ...state.data?.[policyNumber],
                    policySnapshot,
                },
            },
        };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadDCPolicyFailure, (state, { error }) => {
        const newState = { ...state, dcPolicyLoading: false, error };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadDCBillingSuccess, (state, { policyNumber, billingSnapshot }) => {
        const newState = {
            ...state,
            dcBillingLoading: false,
            data: {
                ...state.data,
                [policyNumber]: {
                    ...state.data?.[policyNumber],
                    billingSnapshot,
                },
            },
        };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadDCBillingFailure, (state, { error }) => {
        const newState = { ...state, dcBillingLoading: false, error };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadCommSuccess, (state, { policyNumber, inOutCommSnapshot }) => {
        const newState = {
            ...state,
            commLoading: false,
            data: {
                ...state.data,
                [policyNumber]: {
                    ...state.data?.[policyNumber],
                    inOutCommSnapshot,
                },
            },
        };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadCommFailure, (state, { error }) => {
        const newState = { ...state, commLoading: false, error };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadDMSSuccess, (state, { policyNumber, dmsSnapshot }) => {
        const newState = {
            ...state,
            dmsLoading: false,
            data: {
                ...state.data,
                [policyNumber]: {
                    ...state.data?.[policyNumber],
                    dmsSnapshot,
                },
            },
        };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadDMSFailure, (state, { error }) => {
        const newState = { ...state, dmsLoading: false, error };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadClaimsSuccess, (state, { policyNumber, claimsSnapshot }) => {
        const newState = {
            ...state,
            claimsLoading: false,
            data: {
                ...state.data,
                [policyNumber]: {
                    ...state.data?.[policyNumber],
                    claimsSnapshot,
                },
            },
        };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadClaimsFailure, (state, { error }) => {
        const newState = { ...state, claimsLoading: false, error };
        return {
            ...newState,
            loading:
                newState.dcPolicyLoading ||
                newState.dcBillingLoading ||
                newState.commLoading ||
                newState.dmsLoading ||
                newState.claimsLoading,
        };
    }),
    on(PolicyActions.loadPayPlanSuccess, (state, { policyNumber, payPlanSnapshot }) => ({
        ...state,
        payPlanLoading: false,
        data: {
            ...state.data,
            [policyNumber]: {
                ...state.data?.[policyNumber],
                payPlanSnapshot,
            },
        },
    })),
    on(PolicyActions.loadPayPlanFailure, (state, { error }) => ({
        ...state,
        payPlanLoading: false,
        error,
    })),
    on(PolicyActions.retryPayPlan, (state) => ({
        ...state,
        payPlanLoading: true,
        error: null,
    })),
    on(PolicyActions.verifyContactSuccess, (state, { policyNumber, insuredPhoneValid, insuredPhoneCellular, insuredEmailValid, producerPhoneValid, producerPhoneCellular, producerEmailValid, additionalInsuredValidation, additionalInterestValidation, designatedThirdPartyValidation, escrowValidation, coOwnerValidation }) => ({
        ...state,
        data: {
            ...state.data,
            [policyNumber]: {
                ...state.data?.[policyNumber],
                contactValidation: { insuredPhoneValid, insuredPhoneCellular, insuredEmailValid, producerPhoneValid, producerPhoneCellular, producerEmailValid, additionalInsuredValidation, additionalInterestValidation, designatedThirdPartyValidation, escrowValidation, coOwnerValidation },
            },
        },
    })),
    // Retry individual services
    on(PolicyActions.retryDCPolicy, (state) => ({
        ...state,
        dcPolicyLoading: true,
        error: null,
    })),
    on(PolicyActions.retryDCBilling, (state) => ({
        ...state,
        dcBillingLoading: true,
        error: null,
    })),
    on(PolicyActions.retryComm, (state) => ({
        ...state,
        commLoading: true,
        error: null,
    })),
    on(PolicyActions.retryDMS, (state) => ({
        ...state,
        dmsLoading: true,
        error: null,
    })),
    on(PolicyActions.retryClaims, (state) => ({
        ...state,
        claimsLoading: true,
        error: null,
    }))
);
